import clsx from 'clsx';
import {
    DropdownChangeEvent as PrimeDropdownChangeEvent,
    DropdownProps as PrimeDropdownProps,
    Dropdown as PrimereactDropdown,
} from 'primereact/dropdown';
import { forwardRef, ReactNode, Ref, useImperativeHandle, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { getPlaceholder } from '../../_utils/get-placeholder';
import {
    buildRhfRules,
    type FieldValidate,
    mergeRefs,
    warnAboutRegisterSpread,
} from '../../_utils/rhf-ref';
import {
    InputWrapper,
    InputWrapperSharedProps,
} from '../InputWrapper/InputWrapper';
import { useSelectOverflowBehavior } from '../../_utils/SelectOverflowBehavior';
export { type SelectItemOptionsType } from 'primereact/selectitem/selectitem';
export type DropdownChangeEvent = PrimeDropdownChangeEvent;

export type DropdownProps<T> = PrimeDropdownProps & {
    name?: string;
    valueGetter?: (event: DropdownChangeEvent) => T;
    prefixIcon?: ReactNode;
    outlined?: boolean;
    /**
     * Custom react-hook-form validation function
     */
    validate?: FieldValidate;
} & InputWrapperSharedProps;

function DropdownComponent<T>(
    props: DropdownProps<T>,
    ref: Ref<PrimereactDropdown>,
): ReactNode {
    const {
        name,
        value,
        horizontal,
        labelClassName,
        onBlur,
        valueGetter: __,
        wrapperClassName: ___,
        errorClassName: ____,
        placeholder: _____,
        filter: ______,
        labelTooltip: _______,
        labelSuffix: ________,
        labelHidden: _________,
        bulkMode,
        outlined,
        validate,
        'data-testid': dataTestId,
        ...restProps
    } = props;

    const innerRef = useRef<PrimereactDropdown>(null);

    const minLengthForFilter = 6;
    const testId = dataTestId || name;

    const placeholder = getPlaceholder({ ...props, isDropdown: true });
    const formContext = useFormContext();
    const isControlled = typeof value !== 'undefined';
    const isRHFControlled = !isControlled && Boolean(formContext && name);
    useImperativeHandle(
        isRHFControlled ? null : ref,
        () => innerRef.current as PrimereactDropdown,
        [],
    );
    warnAboutRegisterSpread({
        componentName: 'Dropdown',
        isRHFControlled,
        onChange: restProps.onChange,
        ref,
    });
    const prefixIconTemplate = props.prefixIcon ? (
        <span className="p-inputgroup-addon">{props.prefixIcon}</span>
    ) : null;
    const { resolvedPanelClassName, itemTemplate, valueTemplate } =
        useSelectOverflowBehavior(restProps);
    const hasInvalidState = Boolean(props.errorMessage || props.invalid);
    const { className: restClassName, ...restDropdownProps } = restProps;

    const handleHideOnClear = (e: DropdownChangeEvent) => {
        if (!e.value && props.showClear && innerRef.current) {
            innerRef.current.hide();
        }
    };

    if (!isRHFControlled) {
        const {
            wrapperClassName: _,
            valueGetter: __,
            labelClassName: ___,
            labelTooltip: ____,
            errorClassName: _____,
            panelClassName: _______,
            panelStyle: ________,
            labelSuffix: _________,
            labelHidden: ___________,
            className: customClassName,
            validate: __________,
            ...dropdownProps
        } = props;

        return (
            <InputWrapper
                {...props}
                wrapperClassName={clsx(
                    props.wrapperClassName,
                    outlined && 'input-wrapper--outlined',
                )}
            >
                <div className="p-inputgroup flex-1">
                    {prefixIconTemplate}
                    <PrimereactDropdown
                        pt={{ root: { 'data-testid': testId } }}
                        className={clsx(
                            'flex grow',
                            customClassName,
                            hasInvalidState && 'p-invalid',
                        )}
                        ref={innerRef}
                        name={name}
                        placeholder={placeholder}
                        filter={
                            restProps.options &&
                            restProps.options.length > minLengthForFilter
                        }
                        filterPlaceholder="Search.."
                        {...dropdownProps}
                        appendTo={document.body}
                        panelClassName={resolvedPanelClassName}
                        valueTemplate={valueTemplate}
                        invalid={hasInvalidState}
                        onChange={(e) => {
                            handleHideOnClear(e);
                            if (props.onChange) {
                                props.onChange(e);
                            }
                        }}
                    />
                </div>
            </InputWrapper>
        );
    }

    return (
        <Controller
            name={name!}
            control={formContext!.control}
            rules={buildRhfRules({
                required: props.required,
                bulkMode,
                validate,
            })}
            render={({ field, fieldState }) => (
                <InputWrapper
                    field={field}
                    fieldState={fieldState}
                    horizontal={horizontal}
                    labelClassName={labelClassName}
                    name={name}
                    {...props}
                    wrapperClassName={clsx(
                        props.wrapperClassName,
                        outlined && 'input-wrapper--outlined',
                    )}
                >
                    <div className="p-inputgroup flex-1">
                        {prefixIconTemplate}
                        <PrimereactDropdown
                            pt={{ root: { 'data-testid': testId } }}
                            className={clsx(
                                'flex flex-grow-1',
                                restClassName,
                                fieldState?.invalid || props.invalid
                                    ? 'p-invalid'
                                    : null,
                            )}
                            {...restDropdownProps}
                            {...field}
                            name={name}
                            appendTo={document.body}
                            filter={
                                restProps.options &&
                                restProps.options.length > minLengthForFilter
                            }
                            filterPlaceholder="Search.."
                            panelClassName={resolvedPanelClassName}
                            placeholder={placeholder}
                            invalid={fieldState?.invalid || props.invalid}
                            value={field?.value || props?.value || ''}
                            onChange={(e) => {
                                handleHideOnClear(e);
                                if (props.valueGetter) {
                                    field.onChange(props.valueGetter(e));
                                } else {
                                    field.onChange(e);
                                }

                                if (props.onChange) {
                                    props.onChange(e);
                                }
                            }}
                            onBlur={(event) => {
                                field.onBlur();
                                onBlur?.(event);
                            }}
                            ref={mergeRefs(innerRef, field.ref)}
                            itemTemplate={itemTemplate}
                            valueTemplate={valueTemplate}
                        />
                    </div>
                </InputWrapper>
            )}
        />
    );
}

export { type SelectItem } from 'primereact/selectitem/selectitem';

export const Dropdown = forwardRef(DropdownComponent);
